import { TFunction } from 'i18next';
import { GENERAL__ERROR_MESSAGE } from 'translations/constants';
import { fillPlaceholders } from 'utils/fill-placeholders';

/** Where a failure should leave the user. */
export type MfaFailureAction = 'stay' | 'back-to-login';

export interface MfaFailure {
  /**
   * Translation key to display. The backend's `[MFA] …` strings are already backend-managed
   * translation keys, so they resolve through `t()` directly — there is no client-side error map to
   * keep in sync. Same approach as `utils/kiosk-error.ts`.
   */
  messageKey: string;
  /**
   * Values for the `{{Name}}` placeholders in that translation (e.g. `{{RetryAfterSeconds}}` in
   * `[MFA] TOO_MANY_ATTEMPTS`), taken from the error details. Show a failure with `translateMfaFailure`.
   */
  messageParams: Record<string, string | number>;
  action: MfaFailureAction;
  /** Seconds to keep submit disabled. 0 when the failure is not a rate limit. */
  retryAfterSeconds: number;
}

/**
 * Shared by both MFA flows: first-time enrollment (`mfa-enrollment/`) and the returning-user
 * challenge (`mfa-challenge/`). Nothing here is specific to either.
 *
 * Failures the user cannot recover from on the screen they are on: the MFA session is gone, someone
 * else finished first, setup never started, or every backup code has been spent. All of them mean
 * "go back to login". During a challenge only SESSION_EXPIRED and BACKUP_CODES_EXHAUSTED can occur;
 * the other two are enrollment-only.
 *
 * Matched with `endsWith` against the backend's full key (e.g. "[MFA] SESSION_EXPIRED"), so a
 * longer token that merely contains one of these does not get misrouted, while a change to the
 * "[MFA] " prefix does not silently strand the user on a dead screen.
 */
const BACK_TO_LOGIN_KEYS = [
  'SESSION_EXPIRED',
  'ALREADY_ENROLLED',
  'SETUP_UNAVAILABLE',
  // 409 from the backup-code endpoint: all eight codes are spent. Unlike a wrong code this is not
  // retryable — there is no self-service way to get new ones — so the user is sent back to login
  // with the "contact your employer" copy rather than left on a field that can no longer succeed.
  'BACKUP_CODES_EXHAUSTED',
];

/** The only failure that carries a backoff. Matched like the keys above. */
const RATE_LIMIT_KEY = 'TOO_MANY_ATTEMPTS';

/**
 * Keeps only digits and caps at six, so a pasted "123 456" or "123a456" becomes "123456".
 *
 * This is the *only* length cap on a code field. Do not add `maxLength` to the input as well: the
 * DOM applies it to the raw string before React sees the change, which silently eats a digit from
 * any paste containing a separator.
 */
export const normaliseCode = (raw: string): string => (raw ?? '').replace(/\D/g, '').slice(0, 6);

export const isCompleteCode = (code: string): boolean => normaliseCode(code).length === 6;

/**
 * Backup codes are two groups of four over Crockford base32 **without I, L, O and U** — those four
 * are excluded so a printed code cannot be misread. Do not "helpfully" map O to 0 or I to 1: the
 * server does not, and a code containing either normalises one character short and is rejected as a
 * format error.
 */
const BACKUP_CODE_DISALLOWED = /[^0-9A-HJKMNP-TV-Z]/g;
const BACKUP_CODE_LENGTH = 8;
const BACKUP_CODE_GROUP_LENGTH = 4;

/**
 * Mirrors the server's normalisation and re-inserts the hyphen so the field reads like the printout:
 * uppercase, drop everything outside the alphabet (spaces and the hyphen included), cap at eight.
 *
 * The hyphen is only added once there is something after it, so backspacing never fights the user by
 * re-adding a separator they just deleted.
 *
 * As with `normaliseCode`, this is the *only* length cap — do not add `maxLength` to the input too.
 */
export const normaliseBackupCode = (raw: string): string => {
  const stripped = (raw ?? '').toUpperCase().replace(BACKUP_CODE_DISALLOWED, '').slice(0, BACKUP_CODE_LENGTH);

  if (stripped.length <= BACKUP_CODE_GROUP_LENGTH) {
    return stripped;
  }
  return `${stripped.slice(0, BACKUP_CODE_GROUP_LENGTH)}-${stripped.slice(BACKUP_CODE_GROUP_LENGTH)}`;
};

/**
 * Enables submit; it is not validation. The server re-normalises whatever arrives, so this only
 * spares the user a round trip that could never have succeeded — and, unlike a rejected attempt,
 * costs them nothing against the shared backoff.
 */
export const isCompleteBackupCode = (code: string): boolean =>
  normaliseBackupCode(code).replace('-', '').length === BACKUP_CODE_LENGTH;

/**
 * Reads the backoff. `Retry-After` wins; `details.retryAfterSeconds` is the fallback. RFC allows
 * Retry-After to be an HTTP date, which is useless to a countdown, so anything non-numeric is
 * treated as absent rather than allowed to become NaN.
 */
export const readRetryAfterSeconds = (error: unknown): number => {
  const axiosError = error as {
    response?: {
      headers?: Record<string, string>;
      data?: { details?: { retryAfterSeconds?: number } };
    };
  };

  const header = Number(axiosError?.response?.headers?.['retry-after']);
  if (Number.isFinite(header) && header > 0) {
    return header;
  }

  const detail = Number(axiosError?.response?.data?.details?.retryAfterSeconds);
  return Number.isFinite(detail) && detail > 0 ? detail : 0;
};

/**
 * The error details as placeholder values. `Retry-After` wins over `details.retryAfterSeconds` here too, so the
 * sentence and the countdown on the button always agree.
 */
const readMessageParams = (error: unknown, retryAfterSeconds: number): Record<string, string | number> => {
  const axiosError = error as { response?: { data?: { details?: Record<string, unknown> } } };
  const params: Record<string, string | number> = {};

  Object.entries(axiosError?.response?.data?.details ?? {}).forEach(([name, value]) => {
    if (typeof value === 'string' || typeof value === 'number') {
      params[name] = value;
    }
  });

  if (retryAfterSeconds > 0) {
    params.retryAfterSeconds = retryAfterSeconds;
  }

  return params;
};

export const resolveMfaFailure = (error: unknown): MfaFailure => {
  const axiosError = error as { response?: { data?: { error?: string } } };
  const key = axiosError?.response?.data?.error ?? '';

  // No error key at all: offline, timed out, or an unexpected shape — including ASP.NET's ModelState
  // body for a missing `code`, which client-side validation should keep unreachable.
  if (!key) {
    return { messageKey: GENERAL__ERROR_MESSAGE, messageParams: {}, action: 'stay', retryAfterSeconds: 0 };
  }

  const action: MfaFailureAction = BACK_TO_LOGIN_KEYS.some(candidate => key.endsWith(candidate))
    ? 'back-to-login'
    : 'stay';

  const retryAfterSeconds = key.endsWith(RATE_LIMIT_KEY) ? readRetryAfterSeconds(error) : 0;

  return { messageKey: key, messageParams: readMessageParams(error, retryAfterSeconds), action, retryAfterSeconds };
};

/** The text to show for a failure: the translation of its key, with the backend's `{{Name}}` placeholders filled. */
export const translateMfaFailure = (t: TFunction, failure: MfaFailure): string =>
  fillPlaceholders(t(failure.messageKey), failure.messageParams);
